import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Search as SearchIcon, Tag, TrendingUp, BookOpen, Calendar } from "lucide-react";
import { useState } from "react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import PageHero from "../components/shared/PageHero";
import { BLOG_POSTS_FULL } from "../data/blogPosts";

const ALL_POSTS = BLOG_POSTS_FULL;
const CATS = ["All", "ASO Playbook", "Growth", "GEO", "Retention", "UA"];

const CATEGORY_DESCRIPTIONS = {
  "ASO Playbook": "Deep-dives into App Store metadata, screenshots & keyword strategy.",
  "Growth": "Case studies and sprint breakdowns from real client campaigns.",
  "GEO": "Generative Engine Optimization — get cited by AI assistants.",
  "Retention": "Push, onboarding, and lifecycle tactics that reduce churn.",
  "UA": "Apple Search Ads and paid UA playbooks.",
};

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function Sidebar({ currentSlug }) {
  const sorted = [...ALL_POSTS].sort(
    (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
  );
  const latestNews = sorted.slice(0, 4);

  const otherArticles = ALL_POSTS.filter((p) => p.slug !== currentSlug).slice(0, 4);

  const categoryCounts = CATS.filter((c) => c !== "All").map((c) => ({
    name: c,
    count: ALL_POSTS.filter((p) => p.category === c).length,
    description: CATEGORY_DESCRIPTIONS[c],
  }));

  return (
    <aside className="space-y-8">
      {/* Latest News */}
      <div className="border border-zinc-200 bg-white">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-electric" />
          <h3 className="font-display font-bold text-charcoal text-sm tracking-wide uppercase">
            Latest News
          </h3>
        </div>
        <ul className="divide-y divide-zinc-100">
          {latestNews.map((post) => (
            <li key={post.slug}>
              <Link
                to={`/blog/${post.slug}`}
                className="flex gap-3 p-4 group hover:bg-zinc-50 transition-colors"
              >
                <div className="w-14 h-14 shrink-0 overflow-hidden border border-zinc-200">
                  <img
                    src={post.cover}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-zinc-500 flex items-center gap-1 mb-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(post.publishedAt)}
                  </p>
                  <p className="text-sm font-semibold text-charcoal leading-snug line-clamp-2 group-hover:text-electric transition-colors">
                    {post.title}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Categories */}
      <div className="border border-zinc-200 bg-white">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <Tag className="w-4 h-4 text-electric" />
          <h3 className="font-display font-bold text-charcoal text-sm tracking-wide uppercase">
            Categories
          </h3>
        </div>
        <ul className="divide-y divide-zinc-100">
          {categoryCounts.map((cat) => (
            <li key={cat.name}>
              <Link
                to={`/blog?cat=${encodeURIComponent(cat.name)}`}
                className="flex items-center justify-between px-5 py-3.5 group hover:bg-zinc-50 transition-colors"
              >
                <div>
                  <span className="text-sm font-semibold text-charcoal group-hover:text-electric transition-colors">
                    {cat.name}
                  </span>
                  <p className="text-xs text-zinc-500 mt-0.5 leading-snug line-clamp-1">
                    {cat.description}
                  </p>
                </div>
                <span className="ml-3 shrink-0 text-xs font-bold bg-zinc-100 text-zinc-600 px-2 py-0.5 border border-zinc-200">
                  {cat.count}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Other Articles */}
      <div className="border border-zinc-200 bg-white">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-electric" />
          <h3 className="font-display font-bold text-charcoal text-sm tracking-wide uppercase">
            Other Articles
          </h3>
        </div>
        <ul className="divide-y divide-zinc-100">
          {otherArticles.map((post) => (
            <li key={post.slug}>
              <Link
                to={`/blog/${post.slug}`}
                className="flex items-start gap-3 px-5 py-4 group hover:bg-zinc-50 transition-colors"
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                <div>
                  <span className="text-overline text-electric text-[10px] mb-1 block">
                    {post.category}
                  </span>
                  <p className="text-sm font-semibold text-charcoal leading-snug group-hover:text-electric transition-colors">
                    {post.title}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-zinc-500">
                    <Clock className="w-3 h-3" /> {post.readTime}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Newsletter CTA */}
      <div className="border border-zinc-200 bg-charcoal text-white p-5">
        <h3 className="font-display font-bold text-base mb-1">
          Weekly ASO Playbook
        </h3>
        <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
          Field notes from the frontline, straight to your inbox. No fluff.
        </p>
        <div className="flex flex-col gap-2">
          <input
            type="email"
            placeholder="you@company.com"
            className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2.5 placeholder:text-zinc-500 focus:outline-none focus:border-electric"
          />
          <button className="w-full bg-electric text-white text-sm font-semibold py-2.5 hover:bg-[#42A5F5] transition-colors">
            Subscribe Free
          </button>
        </div>
      </div>
    </aside>
  );
}

export default function BlogPage() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  const filtered = ALL_POSTS.filter(
    (p) =>
      (cat === "All" || p.category === cat) &&
      (!q || p.title.toLowerCase().includes(q.toLowerCase()))
  );

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <Helmet>
        <title>
          Lumotis Blog — ASO, App Marketing & Mobile Growth Playbooks
        </title>
        <meta
          name="description"
          content="Field notes from Lumotis's ASO and app marketing operators. Playbooks on App Store Optimization, user acquisition, retention and GEO."
        />
        <link rel="canonical" href="https://lumotis.com/blog" />
      </Helmet>
      <Navbar />
      <main data-testid="blog-page">
        <PageHero
          testid="blog-hero"
          eyebrow="Blog"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Blog" }]}
          title="Field notes from the"
          titleAccent="ASO frontline."
          subtitle="Weekly playbooks, teardowns and growth notes from the team that ships #1 rankings for 100+ apps across 15+ markets."
        />

        {/* Sticky filter bar */}
        <section className="py-6 bg-white border-b border-zinc-200 sticky top-16 md:top-20 z-30">
          <div className="container flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2 text-sm font-medium border transition-colors ${
                    cat === c
                      ? "bg-charcoal text-white border-charcoal"
                      : "border-zinc-300 text-zinc-700 hover:border-charcoal"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 border border-zinc-300 px-3 py-2 bg-white focus-within:border-electric">
              <SearchIcon className="w-4 h-4 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search posts…"
                data-testid="blog-search"
                className="outline-none bg-transparent text-sm w-48"
                aria-label="Search blog posts"
              />
            </label>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="container">
            {filtered.length === 0 ? (
              <p className="text-center py-20 text-zinc-500">
                No posts match your filter.
              </p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 xl:gap-14">

                {/* Main content column */}
                <div>
                  {/* Featured post */}
                  {featured && (
                    <motion.article
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4 }}
                      className="group mb-10"
                      data-testid="blog-featured"
                    >
                      <Link
                        to={`/blog/${featured.slug}`}
                        className="border border-zinc-200 bg-white hover:border-charcoal transition-colors flex flex-col md:flex-row h-full"
                      >
                        <div className="md:w-1/2 aspect-[16/10] md:aspect-auto overflow-hidden bg-zinc-100 border-b md:border-b-0 md:border-r border-zinc-200 shrink-0">
                          <img
                            src={featured.cover}
                            alt={`${featured.title} — Lumotis blog`}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                            loading="eager"
                            width="600"
                            height="375"
                          />
                        </div>
                        <div className="p-7 flex flex-col flex-1 justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-overline text-electric">
                                {featured.category}
                              </span>
                              <span className="w-1 h-1 rounded-full bg-zinc-300" />
                              <span className="text-xs text-zinc-500">
                                {formatDate(featured.publishedAt)}
                              </span>
                            </div>
                            <h2 className="font-display text-2xl md:text-3xl font-bold text-charcoal leading-snug">
                              {featured.title}
                            </h2>
                            <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                              {featured.excerpt}
                            </p>
                          </div>
                          <div className="mt-6 pt-5 border-t border-zinc-200 flex items-center justify-between">
                            <span className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" /> {featured.readTime}
                            </span>
                            <span className="inline-flex items-center gap-1 text-sm text-charcoal font-semibold group-hover:text-electric transition-colors">
                              Read full article <ArrowUpRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  )}

                  {/* Remaining posts — 2-column grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {rest.map((p, idx) => (
                      <motion.article
                        key={p.slug}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: idx * 0.05 }}
                        className="group"
                        data-testid={`blog-card-${idx}`}
                      >
                        <Link
                          to={`/blog/${p.slug}`}
                          className="border border-zinc-200 bg-white hover:border-charcoal transition-colors flex flex-col h-full"
                        >
                          <div className="aspect-[16/10] overflow-hidden bg-zinc-100 border-b border-zinc-200">
                            <img
                              src={p.cover}
                              alt={`${p.title} — Lumotis blog`}
                              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                              loading="lazy"
                              width="600"
                              height="375"
                            />
                          </div>
                          <div className="p-5 flex flex-col flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-overline text-electric">
                                {p.category}
                              </span>
                              <span className="w-1 h-1 rounded-full bg-zinc-300" />
                              <span className="text-xs text-zinc-500">
                                {formatDate(p.publishedAt)}
                              </span>
                            </div>
                            <h3 className="font-display text-lg font-bold text-charcoal leading-snug">
                              {p.title}
                            </h3>
                            <p className="mt-2 text-sm text-zinc-600 leading-relaxed flex-1 line-clamp-3">
                              {p.excerpt}
                            </p>
                            <div className="mt-5 pt-4 border-t border-zinc-200 flex items-center justify-between text-xs text-zinc-500">
                              <span className="inline-flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5" /> {p.readTime}
                              </span>
                              <span className="inline-flex items-center gap-1 text-charcoal font-semibold group-hover:text-electric transition-colors">
                                Read <ArrowUpRight className="w-3.5 h-3.5" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      </motion.article>
                    ))}
                  </div>
                </div>

                {/* Sidebar */}
                <Sidebar />
              </div>
            )}
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
